import { useEffect, useMemo, useState } from 'react';
import { Backdrop } from '@hyperflake/react-ui-library';
import { AnimatePresence, motion } from 'framer-motion';
import { CrossIcon } from 'modules/shared/icons';
import ScrollLock from 'react-scrolllock';
import { IBoundingBox } from '@api/models/analytics.model';
import useBoxDimensions from '@hooks/use-box-dimensions';
import { IResolution } from '@api/types/resolution.type';

const modalAnimation = {
    variants: {
        hidden: { scale: 0.15, opacity: 0 },
        visible: { scale: 1, opacity: 1 },
    },
    transition: { duration: 0.35 },
    initial: 'hidden',
    animate: 'visible',
    exit: 'hidden',
};

interface ImageSneakPeekModalProps {
    show: boolean;
    onClose: () => void;
    imageName: string;
    imageUrl: string;
    boundingBox: IBoundingBox;
    resolution: IResolution;
}

const ImageSneakPeekModal = ({
    show,
    onClose,
    boundingBox,
    imageName,
    imageUrl,
    resolution,
}: ImageSneakPeekModalProps) => {
    // Close on ESC
    useEffect(() => {
        if (!show) return;
        const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [show, onClose]);

    const { aspectRatio, headerHeight, containerWidth, containerHeight } = useMemo(() => {
        const headerHeight = 60;

        return {
            aspectRatio: resolution?.width / resolution?.height || 1.49,
            headerHeight: 60,
            containerWidth: window.innerWidth - 96,
            containerHeight: window.innerHeight - 96 - headerHeight,
        };
    }, []);

    const { refs, dimensions } = useBoxDimensions({ aspectRatio, containerWidth, containerHeight });

    const selectedImageBoundingBox = useMemo(() => {
        if (!boundingBox)
            return {
                left: '0%',
                top: '0%',
                width: '0%',
                height: '0%',
                confidence: 0,
            };

        const getWidthPercentage = (width: number) => width / resolution.width;
        const getHeightPercentage = (height: number) => height / resolution.height;

        return {
            left: `${getWidthPercentage(boundingBox.x1) * 100}%`,
            top: `${getHeightPercentage(boundingBox.y1) * 100}%`,
            width: `${getWidthPercentage(boundingBox.x2 - boundingBox.x1) * 100}%`,
            height: `${getHeightPercentage(boundingBox.y2 - boundingBox.y1) * 100}%`,
            confidence: boundingBox.confidence,
        };
    }, [boundingBox, resolution]);

    return (
        <AnimatePresence initial={false} mode="wait">
            {show && (
                <>
                    <Backdrop ref={refs.setReference} show={show} onClick={onClose} className="z-[30]" />

                    <ScrollLock>
                        <div
                            className="fixed left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-[31] mt-5"
                            style={{ height: dimensions.height + headerHeight, width: dimensions.width }}
                        >
                            <motion.div
                                {...modalAnimation}
                                onClick={(e) => e.stopPropagation()}
                                role="dialog"
                                aria-modal="true"
                                aria-label={imageName}
                            >
                                <div className=" bg-card-color rounded-2xl overflow-hidden flex flex-col items-center shadow-xl ">
                                    {/* Header */}
                                    <div
                                        className="flex items-center justify-between px-6 h-12 border-b border-black/5 "
                                        style={{ height: headerHeight }}
                                    >
                                        <h5 className="font-bold text-grayscale-900 truncate">{imageName}</h5>
                                        <div>
                                            <button
                                                className="grid place-items-center w-7 h-7 rounded-full bg-background-color hover:opacity-80 transition"
                                                onClick={onClose}
                                                aria-label="Close"
                                            >
                                                <CrossIcon className="text-grayscale-500" width="10" height="10" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Content area fills remaining height */}
                                    <div className="relative flex-1 bg-black/5">
                                        {imageUrl ? (
                                            <img
                                                src={imageUrl}
                                                alt={imageName}
                                                className="w-full h-full object-contain"
                                            />
                                        ) : (
                                            <div className="absolute inset-0 bg-grayscale-100 flex items-center justify-center z-[3]">
                                                {/* placeholder SVG (unchanged) */}
                                                <svg
                                                    width="20%"
                                                    viewBox="0 0 114 70"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                >
                                                    <path
                                                        d="M113.959 2.548v64.838a1.632 1.632 0 01-1.631 1.632H2.261A1.63 1.63 0 01.63 67.386V2.548A1.632 1.632 0 012.26.916h110.067a1.63 1.63 0 011.632 1.631v.001z"
                                                        fill="#C8DDFA"
                                                    />
                                                    <path
                                                        d="M28.372 39.467c7.909 0 14.32-6.412 14.32-14.32 0-7.91-6.411-14.322-14.32-14.322-7.91 0-14.321 6.412-14.321 14.321 0 7.91 6.411 14.321 14.32 14.321z"
                                                        fill="#000"
                                                    />
                                                    <path
                                                        d="M73.282 69.278H3.028a1.435 1.435 0 01-.262-.02l33.227-57.555a2.327 2.327 0 014.05 0l22.299 38.624 1.068 1.848 9.873 17.103z"
                                                        fill="#8EBBFE"
                                                    />
                                                    <path
                                                        opacity={0.2}
                                                        d="M73.28 69.278H49.292l11.775-17.104.847-1.231.424-.617 1.068 1.849 9.872 17.103z"
                                                        fill="#000"
                                                    />
                                                    <path
                                                        d="M112.397 69.278H51.633l11.775-17.104.847-1.232L79.6 28.653c1.006-1.46 3.426-1.552 4.608-.275.08.087.154.179.221.275l27.969 40.625z"
                                                        fill="#8EBBFE"
                                                    />
                                                    <path
                                                        d="M31.104 46.699c.7 0 1.268-.564 1.268-1.26 0-.695-.568-1.26-1.268-1.26-.7 0-1.268.565-1.268 1.26 0 .696.568 1.26 1.268 1.26z"
                                                        fill="#000"
                                                    />
                                                    <path
                                                        d="M33.182 45.3a2.075 2.075 0 00-2.075-1.93 2.075 2.075 0 00-2.076 1.93h4.151zM44.327 46.965c.7 0 1.268-.564 1.268-1.26 0-.695-.568-1.259-1.268-1.259-.7 0-1.268.564-1.268 1.26 0 .695.567 1.26 1.268 1.26z"
                                                        fill="#000"
                                                    />
                                                    <path
                                                        d="M46.401 45.568a2.075 2.075 0 00-2.076-1.93 2.075 2.075 0 00-2.075 1.93h4.151z"
                                                        fill="#000"
                                                    />
                                                    <path
                                                        d="M34.621 52.995s4.262-1.6 7.389 1.721M42.615 54.39s-.753-.352-.744 1.151M42.613 40.149s2.087-.721 3.583 1.01M29 41.035s1.509-1.602 3.644-.762"
                                                        stroke="#000"
                                                        strokeWidth={1.17275}
                                                        strokeMiterlimit={10}
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                    <path
                                                        d="M75.104 53.699c.7 0 1.268-.564 1.268-1.26 0-.695-.568-1.26-1.268-1.26-.7 0-1.268.565-1.268 1.26 0 .696.568 1.26 1.268 1.26z"
                                                        fill="#000"
                                                    />
                                                    <path
                                                        d="M77.182 52.3a2.075 2.075 0 00-2.075-1.93 2.075 2.075 0 00-2.076 1.93h4.151zM88.327 53.965c.7 0 1.268-.564 1.268-1.26 0-.695-.568-1.259-1.268-1.259-.7 0-1.268.564-1.268 1.26 0 .695.567 1.26 1.268 1.26z"
                                                        fill="#000"
                                                    />
                                                    <path
                                                        d="M90.401 52.568a2.075 2.075 0 00-2.075-1.93 2.075 2.075 0 00-2.076 1.93h4.151z"
                                                        fill="#000"
                                                    />
                                                    <path
                                                        d="M78.621 59.995s4.262-1.6 7.389 1.721M86.615 61.39s-.753-.352-.744 1.151M86.613 47.149s2.087-.721 3.583 1.01M73 48.035s1.509-1.602 3.644-.762"
                                                        stroke="#000"
                                                        strokeWidth={1.17275}
                                                        strokeMiterlimit={10}
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            </div>
                                        )}

                                        {/* Only render bounding box overlay if image dimensions are available */}

                                        <div
                                            className="group relative cursor-pointer"
                                            style={{
                                                position: 'absolute',
                                                left: selectedImageBoundingBox.left,
                                                top: selectedImageBoundingBox.top,
                                                width: selectedImageBoundingBox.width,
                                                height: selectedImageBoundingBox.height,
                                            }}
                                            //   onClick={() => handleBoundingBoxClick(box)}
                                        >
                                            <div className="absolute inset-0 border-[1.5px] border-[#12b76a] rounded-[2px] group-hover:brightness-125" />

                                            <div
                                                className="absolute -top-8 left-0 bg-[#12b76a] px-1 py-0.5 text-[8px] rounded whitespace-nowrap z-50 flex flex-col items-center group-hover:brightness-125 group-hover:scale-105 group-hover:shadow-md"
                                                style={{ color: '#000000' }}
                                            >
                                                <span>Confidence</span>
                                                <span>{selectedImageBoundingBox.confidence}%</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </ScrollLock>
                </>
            )}
        </AnimatePresence>
    );
};

export default ImageSneakPeekModal;
